import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import {
  useGetProteinTypesQuery,
  useCreateProteinTypeMutation,
  useUpdateProteinTypeMutation,
  useDeleteProteinTypeMutation,
} from "../../store/api";

const ProteinTypes = () => {
  const { data, isLoading, isError, error } = useGetProteinTypesQuery();
  const [createProteinType, createState] = useCreateProteinTypeMutation();
  const [updateProteinType, updateState] = useUpdateProteinTypeMutation();
  const [deleteProteinType, deleteProteinTypeState] =
    useDeleteProteinTypeMutation();

  const [form, setForm] = useState({ name: "", code: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    proteinTypeId: null,
    proteinTypeName: "",
  });

  const proteinTypes = Array.isArray(data) ? data : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateProteinType({
          proteinTypeId: editingId,
          payload: form,
        }).unwrap();
        setEditingId(null);
      } else {
        await createProteinType(form).unwrap();
      }
      setForm({ name: "", code: "", description: "" });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (proteinType) => {
    setEditingId(proteinType.proteinTypeId || proteinType.id);
    setForm({
      name: proteinType.name || proteinType.proteinTypeName || "",
      code: proteinType.code || "",
      description: proteinType.description || "",
    });
    setShowForm(true);
  };

  const handleDelete = (proteinType) => {
    setDeleteModal({
      isOpen: true,
      proteinTypeId: proteinType.proteinTypeId || proteinType.id,
      proteinTypeName:
        proteinType.name || proteinType.proteinTypeName || "this protein type",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteProteinType(deleteModal.proteinTypeId).unwrap();
    } catch (err) {
      // error handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        proteinTypeId: null,
        proteinTypeName: "",
      });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ name: "", code: "", description: "" });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "code", label: "Code" },
    // { key: "description", label: "Description" },
    { key: "actions", label: "Actions" },
  ];

  const rows = proteinTypes.map((proteinType) => {
    const id = proteinType.proteinTypeId || proteinType.id;
    return {
      id,
      name: proteinType.name || proteinType.proteinTypeName,
      code: proteinType.code || "-",
      description: proteinType.description || "-",
      actions: (
        <div className="flex gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(proteinType)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(proteinType)}
          />
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Protein Types"
        subtitle="Manage protein type classifications for menu items."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Protein Type
            </Button>
          )
        }
      />
      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Protein Type" : "Add Protein Type"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Name"
                name="name"
                placeholder="Chicken"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Code"
                name="code"
                placeholder="CHICKEN"
                value={form.code}
                onChange={handleChange}
                helper="Required unique code used by the backend"
                required
              />
              {/* <Input
                label="Description"
                name="description"
                placeholder="Poultry-based protein"
                value={form.description}
                onChange={handleChange}
              /> */}
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Protein type {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save protein type."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Protein Type
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            All Protein Types
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading protein types...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load protein types."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No protein types yet."
              />
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            proteinTypeId: null,
            proteinTypeName: "",
          })
        }
        title="Delete Protein Type"
        confirmText="Delete"
        onConfirm={confirmDelete}
        isLoading={deleteProteinTypeState.isLoading}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.proteinTypeName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default ProteinTypes;
